import React, { useState, useEffect, useRef } from 'react';
import { t } from '../i18n';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import LangSwitch from './LangSwitch';
import { useNotifications } from '../hooks/useNotifications';
import SearchOverlay from './SearchOverlay';
import { openHelp } from '../help/helpMap';

const NAV_ITEMS = {
  manager:    { label: 'Продажи и заказы',     icon: '🛒' },
  technolog:  { label: 'Очередь производства', icon: '⚙️' },
  quality:    { label: 'Качество: разбор листов', icon: '🔬' },
  operator:   { label: 'Терминал оператора',   icon: '🖥️' },
  sklad_gp:   { label: 'Склад ГП',             icon: '📦' },
  director:   { label: 'Дашборд директора',    icon: '📊' },
  admin:      { label: 'Администратор',         icon: '🔧' },
  controller: { label: 'Контроль качества',    icon: '🔍' },
  buhgalter:  { label: 'Бухгалтерия',          icon: '🧾' },
  investor:   { label: 'Инвестор (только чтение)', icon: '📈' },
};


// ─── System Settings Modal ────────────────────────────────────────────────────────
// Настройки системы жили здесь, в модале под шестерёнкой в шапке. Место
// оказалось неверным: настройка — это не то, что делают на бегу с любой
// страницы, и искать её приходилось наугад. Теперь они в администрировании,
// каждая рядом со своим делом: порог простоя — среди причин остановки,
// приём заказов без сырья — в общих настройках.

// Разделы страницы (sections) рисуются в этом же боковом меню, под пунктом
// текущей страницы. Раньше у страницы было своё меню в самом содержимом: слева
// пустая колонка, а рядом вторая колонка кнопок — место занято дважды, и глазу
// непонятно, какое из двух меню главное.
//
// sections: [{ title, items: [{ key, label, badge?, alert? }] }]
//   label начинается со значка ('📋 Заказы') — в свёрнутом меню остаётся он.

// alsoSections — ключи разделов, которых нет в боковом меню: их открывают
// плиткой с главного экрана страницы. Снаружи (из поиска) они такие же
// настоящие разделы, и ?s= должен их пускать. Без этого списка ссылка на
// «Короткие имена» молча приводила бы на главный экран технолога.
export default function Layout({ children, title, sections, section, onSection,
                                 alsoSections }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const role      = sessionStorage.getItem('role');
  // Цех человека: он же стоит в шапке. Пусто — у входа старого образца
  // (до появления второго цеха) или у того, кому открыт весь завод.
  const branchName = sessionStorage.getItem('branch_name') || '';
  const userName  = sessionStorage.getItem('user_name') || 'Пользователь';
  const [showSearch, setShowSearch] = useState(false);
  const { isDark, toggle } = useTheme();

  const { permission, requestPermission } = useNotifications();

  // Переход СРАЗУ в раздел: /admin?s=users открывает «Пользователей», а не
  // главный экран администрирования.
  //
  // Раздел — это состояние внутри страницы, и снаружи попасть в него было
  // нельзя: поиск, подсказка или ссылка могли довести только до двери, а
  // дальше человек снова искал руками. Ловим ключ здесь, в общем месте, —
  // тогда это работает на любой странице, и переписывать их не нужно.
  //
  // Ключ читаем через ref: sections и onSection — новые объекты на каждый
  // рендер, и в зависимостях эффекта они гоняли бы его без конца.
  const secRef = useRef(sections);
  const onSecRef = useRef(onSection);
  const alsoRef = useRef(alsoSections);
  secRef.current = sections;
  onSecRef.current = onSection;
  alsoRef.current = alsoSections;
  useEffect(() => {
    const want = new URLSearchParams(location.search).get('s');
    if (!want || !onSecRef.current) return;
    const known = (secRef.current || []).some(
      (g) => (g.items || []).some((i) => i.key === want))
      || (alsoRef.current || []).includes(want);
    // Незнакомый ключ не пускаем: страница показала бы пустоту вместо экрана.
    if (!known) return;
    onSecRef.current(want);
    // Ключ убираем из адреса: он уже сработал, а оставшись, он вернул бы
    // человека в тот же раздел при следующем обновлении страницы — даже если
    // тот успел уйти в другой.
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Ctrl+K / Cmd+K → qidiruvni ochish
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(s => !s);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // «Передачи» убраны: их заменила «Приёмка из цеха». Склад забирает смену
  // целиком, по биркам, с историей по каждому листу — а передача добавляла к
  // тому же остатку второй, ничем не связанный путь.

  const handleLogout = () => {
    sessionStorage.clear();
    // Вход в /ai (ai/aiApi.js) хранится отдельно — выходим и из него.
    localStorage.removeItem('ai_access');
    localStorage.removeItem('ai_refresh');
    navigate('/login');
  };

  const roleInfo = NAV_ITEMS[role] || { label: role, icon: '👤' };

  // Право планировщика — личное, поэтому спрашиваем его, а не должность.
  const canPlan = sessionStorage.getItem('can_plan') === '1';

  // Bildirishnoma tugmasi ko'rsatiladigan rollar
  const showNotifBtn = ['technolog', 'sklad_gp', 'operator'].includes(role);

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sb_collapsed') === '1');

  // У кладовщика в руках планшет, а не ноутбук: экран уже, и постоянное меню
  // отъедало треть рабочей ширины — на приёмке и отгрузке там таблицы. На
  // узком экране меню выезжает поверх страницы и закрывается сразу после
  // выбора: открывают его в смену несколько раз, а работают в содержимом.
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(max-width: 1024px)').matches);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const on = (e) => { setNarrow(e.matches); if (!e.matches) setDrawer(false); };
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener
      ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);

  const toggleSidebar = () => {
    if (narrow) { setDrawer((d) => !d); return; }
    setCollapsed((c) => { localStorage.setItem('sb_collapsed', c ? '0' : '1'); return !c; });
  };
  // Переход по пункту меню закрывает ящик: иначе он остаётся поверх страницы,
  // на которую только что перешли, и её не видно.
  const go = (to) => { setDrawer(false); navigate(to); };

  const hdrBtn = { width: 36, height: 36, borderRadius: 9, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--muted)', cursor: 'pointer', fontSize: 15, display: 'grid', placeItems: 'center', fontFamily: 'inherit' };

  // Меню одно на всю программу и делится на разделы: сверху то, чем
  // пользуются каждую смену, ниже — учётные экраны и настройки.
  // Раньше у страницы «Обзор» было СВОЁ боковое меню: стоило нажать в нём
  // любой пункт — открывалась страница с этим меню, и оно «прыгало».
  // В цеху за компьютером обычно не работают, и меню, меняющееся от
  // страницы к странице, читается как сбой.
  // Меню одно на всю программу. Здесь только то, ради чего человек заходит в
  // программу СЕГОДНЯ — по одной двери на роль.
  //
  // Было иначе: у администратора пятнадцать пунктов, у технолога и кладовщика
  // по шесть, и рядом с ними в том же столбце висели разделы самой страницы —
  // двадцать пять и девятнадцать строк одним шрифтом. Каждодневное и то, что
  // открывают раз в месяц, выглядело одинаково, и человек каждый раз спрашивал
  // заново, куда нажимать.
  //
  // Отчёты собраны за одну дверь: их смотрят, когда нужно свериться, а не
  // когда работают. Администратору роли открываются с его главного экрана —
  // держать восемь чужих рабочих мест в общем меню незачем.
  // Значки. Были ▤ ▣ ◧ ▥ ▦ ◈ — геометрические фигуры, которые ничего не
  // называют: в цеху по ним не отличить склад от терминала, и в свёрнутом
  // меню, где остаётся ОДИН значок, пункт становился безымянным.
  //
  // Теперь значок закреплён за смыслом и один и тот же по всей программе:
  //   📦 товар и остаток · 🚚 отгрузка · ⚙️ пресс · 🏷 сорт · 🧾 документ
  //   👥 люди · 🔧 настройка · 📊 цифры · 🏭 цех · 🖥 терминал · ⇆ обмен
  const NAV_GROUPS = [
    {
      // Заголовок обязателен: по нему группа сворачивается и по нему же React
      // отличает одну группу от другой. Двум пустым он дал один ключ — обе
      // свернулись разом, и в меню остались две стрелки без единой подписи.
      title: 'Работа',
      items: [
        { ic: '📊', label: 'Дашборд директора', to: '/director', show: ['director', 'admin'].includes(role) },
        { ic: '🛒', label: 'Продажи и заказы', to: '/manager', show: role === 'manager' },
        // Плановику эти два экрана открыты тем же личным правом, что и
        // действия на них. Без них меню обещало ему только его собственные
        // продажи, а планировать он должен цех целиком.
        { ic: '⚙️', label: 'Очередь производства', to: '/technolog',
          show: role === 'technolog' || canPlan },
        { ic: '🗓', label: 'Заявка на производство', to: '/production-requests',
          show: role === 'technolog' || canPlan },
        // Разбор качества. У технолога по качеству это единственный экран,
        // у остальных — ещё один: вопрос «почему брак» задают и в цехе.
        { ic: '🔬', label: 'Качество: разбор листов', to: '/quality',
          show: ['quality', 'technolog', 'director', 'admin', 'investor'].includes(role) },
        { ic: '🖥', label: 'Терминал оператора', to: '/terminal', show: role === 'operator' },
        { ic: '📦', label: 'Склад ГП', to: '/warehouse', show: role === 'sklad_gp' },
        { ic: '🧾', label: 'Бухгалтерия', to: '/buhgalter', show: role === 'buhgalter' },
        { ic: '🔧', label: 'Администрирование', to: '/admin', show: role === 'admin' },
        { ic: '🤖', label: 'ИИ-помощник', to: '/ai', show: role === 'investor' },
        // Что делается прямо сейчас — общий для всех, кроме продаж
        // Раньше щита у менеджера не было вовсе. А планированием занимается
        // как раз он — точнее тот из менеджеров, кому дали право: решает, что
        // запускать следующим, и решает это глядя на прессы. Отправлять его за
        // этим к технологу значит держать план в чужих руках.
        { ic: '🏭', label: 'Что делается сейчас', to: '/board', show: role !== 'investor' },
      ],
    },
    {
      title: 'Справки',
      items: [
        // ── Выпущенные листы вернулись в меню ──────────────────────────────
        //
        // Их убрали за дверь «Отчёты и справки» вместе с движением ТМЗ и
        // журналом действий — по признаку «открывают не каждый день». Признак
        // оказался неверным: по этим двум экранам разбирают спор о цифре
        // выпуска и ищут, откуда взялся плохой лист, и делают это в цеху
        // постоянно. За общей дверью с безликим именем их просто перестали
        // находить — «окно с листами пропало».
        //
        // Два экрана, потому что и вопроса два: «что вышло с этой смены»
        // (реестр, от продукта к смене и к каждому листу) и «что было в этот
        // день» (смены, цепочка «пресс сдал — склад принял»).
        { ic: '📑', label: 'Выпущенные листы', to: '/registry',
          show: ['director', 'admin', 'sklad_gp', 'technolog', 'buhgalter', 'investor'].includes(role) },
        { ic: '⌛', label: 'Смены по дням', to: '/sessions',
          show: ['director', 'admin', 'sklad_gp', 'technolog', 'buhgalter', 'investor'].includes(role) },
        { ic: '📚', label: 'Отчёты и справки', to: '/reports',
          show: ['director', 'admin', 'sklad_gp', 'technolog', 'buhgalter', 'investor'].includes(role) },
        // Руководство своей роли. Не страница, а отдельная вкладка: читают его
        // не вместо работы, а рядом с ней — рабочий экран закрываться не должен.
        { ic: '📖', label: 'Руководство', action: () => openHelp(null), show: true },
      ],
    },
  ]
    .map((g) => ({ ...g, items: g.items.filter((n) => n.show) }))
    .filter((g) => g.items.length > 0);

  const SEC_GROUPS = (sections || []).filter((g) => g && g.items && g.items.length > 0);

  // Значок = первый символ подписи, если это не буква и не цифра
  const secIcon = (label) => {
    const c = Array.from(String(label).trim())[0] || '';
    return /[\p{L}\p{N}]/u.test(c) ? '·' : c;
  };
  // Единственная группа в меню — заголовок над ней лишний шум
  const showGroupTitles = NAV_GROUPS.length > 1;

  // ── Где я сейчас ──────────────────────────────────────────────────────────
  //
  // В шапке стоял один заголовок, и он далеко не всегда совпадал с тем, что
  // человек нажал: в меню написано «Очередь производства», а сверху —
  // «Технолог — Управление очередью». Открыв раздел, он вообще не менялся:
  // сидишь в «Пользователях», а сверху по-прежнему «Администратор».
  //
  // Теперь дорога видна целиком — «Администрирование › Пользователи», — и
  // первая часть возвращает на главный экран страницы. Раньше возврат был
  // только внутри некоторых страниц, кнопкой «Назад», и вёл он всегда на
  // «Главное», откуда бы человек ни пришёл.
  const navItem = NAV_GROUPS
    .flatMap((g) => g.items)
    .find((n) => n.to === location.pathname);
  const pageLabel = (navItem && navItem.label) || title || roleInfo.label;
  // Подпись раздела идёт со значком ('📋 Заказы') — в крошке он лишний: свой
  // значок уже стоит у страницы, и два подряд читаются как один пункт.
  const stripIcon = (x) => String(x || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
  const allSections = (sections || []).flatMap((g) => (g.items || []));
  // Первый экран страницы называется по-разному: у технолога и склада это
  // 'home', у бухгалтера 'exchange', у директора 'orders'. Жёстко записанное
  // 'home' увело бы половину страниц в несуществующий раздел — то есть в
  // пустоту.
  const homeSection = allSections.find((i) => i.key === 'home') || allSections[0];
  const homeKey = homeSection && homeSection.key;
  const curSection = allSections.find((i) => i.key === section);
  // Первый экран — это и есть страница, второй раз его не называем.
  const crumb = curSection && curSection.key !== homeKey
    ? stripIcon(curSection.label) : '';

  // На планшете меню всегда полной ширины: свёрнутое до значков оно требует
  // прицельного попадания, а его открывают пальцем.
  const SB = narrow ? 268 : (collapsed ? 64 : 238);
  const shrunk = narrow ? false : collapsed;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: narrow ? '1fr' : SB + 'px 1fr',
      minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)',
    }}>
      {/* Затемнение под ящиком: тап мимо меню закрывает его — на планшете это
          привычнее, чем искать крестик */}
      {narrow && drawer && (
        <div onClick={() => setDrawer(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 290,
        }} />
      )}
      <aside style={{
        background: 'var(--card)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        ...(narrow
          ? {
              position: 'fixed', top: 0, left: 0, width: SB, height: '100vh',
              zIndex: 300, transform: drawer ? 'none' : 'translateX(-100%)',
              transition: 'transform .18s ease-out',
              boxShadow: drawer ? '6px 0 26px rgba(0,0,0,.35)' : 'none',
            }
          : { position: 'sticky', top: 0, height: '100vh', transition: 'width .15s' }),
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: shrunk ? '18px 0 14px' : '18px 18px 14px', justifyContent: shrunk ? 'center' : 'flex-start' }}>
          <div title={t("LamTech MES")} style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(150deg,var(--accent),var(--accent-strong))', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 18, flexShrink: 0 }}>&#9635;</div>
          {!shrunk && (
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>LamTech <span style={{ color: 'var(--accent)' }}>MES</span></div>
              <div style={{ fontSize: 11, color: 'var(--dim)', whiteSpace: 'nowrap' }}>
                {branchName ? t('Производство') + ' · ' + branchName : t('Производство')}
              </div>
            </div>
          )}
        </div>
        <nav style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
          {NAV_GROUPS.map((g, gi) => (
            <React.Fragment key={g.title}>
              {/* В свёрнутом меню подписи не помещаются — вместо них черта */}
              {shrunk
                ? gi > 0 && <div style={{ height: 1, background: 'var(--border)', margin: '8px 6px' }} />
                : showGroupTitles && (
                    <div style={{ padding: gi === 0 ? '8px 10px 6px' : '16px 10px 6px', fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--dim)' }}>
                      {t(g.title)}
                    </div>
                  )}
              {/* Свёрнут — но пункт, на котором стоим, остаётся виден: иначе
                  из свёрнутого раздела пропадали бы и разделы самой страницы,
                  а вместе с ними ответ на вопрос «где я сейчас». */}
              {g.items.map((n) => {
                const active = location.pathname === n.to;
                return (
                  <React.Fragment key={n.to || n.label}>
                    <button onClick={() => (n.action ? n.action() : go(n.to))} title={n.label} className="touch-btn" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 11, padding: shrunk ? '10px 0' : '9px 11px', justifyContent: shrunk ? 'center' : 'flex-start', borderRadius: 9, border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: 'inherit', background: active ? 'var(--accent-weak)' : 'transparent', color: active ? 'var(--text)' : 'var(--muted)', fontWeight: 600, fontSize: 13.5 }}>
                      {/* Полоска слева: активный пункт видно и боковым зрением */}
                      {active && <span style={{ position: 'absolute', left: -10, top: 8, bottom: 8, width: 3, borderRadius: '0 3px 3px 0', background: 'var(--accent)' }}></span>}
                      <span style={{ width: 18, textAlign: 'center', fontSize: 15, flexShrink: 0 }}>{n.ic}</span>
                      {!shrunk && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.label}</span>}
                      {!shrunk && n.badge > 0 && <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>{n.badge}</span>}
                      {shrunk && n.badge > 0 && <span style={{ position: 'absolute', top: 4, right: 10, width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }}></span>}
                    </button>

                    {/* Разделы открытой страницы — с отступом под её пунктом,
                        чтобы было видно: это части одной страницы, а не соседние */}
                    {active && SEC_GROUPS.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '2px 0 8px', paddingLeft: shrunk ? 0 : 11, marginLeft: shrunk ? 0 : 19, borderLeft: shrunk ? 'none' : '1px solid var(--border)' }}>
                        {SEC_GROUPS.map((sg, sgi) => (
                          <React.Fragment key={sg.title || sgi}>
                            {!shrunk && sg.title && <div style={{ padding: sgi === 0 ? '4px 10px 4px' : '10px 10px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--dim)' }}>{t(sg.title)}</div>}
                            {sg.items.map((s) => {
                              const on = section === s.key;
                              return (
                                <button key={s.key} onClick={() => onSection && onSection(s.key)} title={s.label} className="touch-btn" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, minHeight: 34, padding: shrunk ? '8px 0' : '7px 10px', justifyContent: shrunk ? 'center' : 'flex-start', borderRadius: 8, border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: 'inherit', background: on ? 'var(--accent)' : 'transparent', color: on ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 13 }}>
                                  {shrunk
                                    ? <span style={{ fontSize: 15 }}>{secIcon(s.label)}</span>
                                    : <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>}
                                  {!shrunk && s.badge > 0 && <span style={{ background: s.alert ? '#ef4444' : (on ? 'rgba(255,255,255,.25)' : 'var(--bg)'), color: (s.alert || on) ? '#fff' : 'var(--muted)', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>{s.badge}</span>}
                                  {shrunk && s.badge > 0 && <span style={{ position: 'absolute', top: 3, right: 8, width: 7, height: 7, borderRadius: '50%', background: s.alert ? '#ef4444' : 'var(--accent)' }}></span>}
                                </button>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </nav>
        <div style={{ padding: shrunk ? '12px 8px' : '12px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, alignItems: shrunk ? 'center' : 'stretch' }}>
          <button onClick={() => navigate('/profile')} title={userName} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: 0, fontFamily: 'inherit', justifyContent: shrunk ? 'center' : 'flex-start' }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--accent-weak)', color: 'var(--accent)', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{userName.slice(0, 2).toUpperCase()}</div>
            {!shrunk && (
              <div style={{ textAlign: 'left', minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>{userName}</div>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>{roleInfo.label}</div>
              </div>
            )}
          </button>
          {/* Раздел поставщика программы. Клиенту его не рисуем вовсе — не
              «показываем неактивным», а не рисуем: о том, что новый цех можно
              открыть, завод знать не должен. */}
          {sessionStorage.getItem('is_platform') === '1' && (
            <button onClick={() => navigate('/platform')} title="Платформа"
              className="touch-btn" style={{ fontSize: 12, fontWeight: 700,
                color: '#8b5cf6', background: '#8b5cf614',
                border: '1px solid #8b5cf640', borderRadius: 8, padding: '6px',
                cursor: 'pointer', fontFamily: 'inherit',
                width: shrunk ? 36 : '100%' }}>
              {shrunk ? '🏢' : '🏢 Платформа'}
            </button>
          )}
          {/* Ключ ИИ вводит владелец платформы и привязывает к клиенту; клиенту
              пункт не рисуем вовсе. */}
          {sessionStorage.getItem('is_superuser') === '1' && (
            <button onClick={() => navigate('/platform/ai')} title="Настройки ИИ"
              className="touch-btn" style={{ fontSize: 12, fontWeight: 700,
                color: '#8b5cf6', background: '#8b5cf614',
                border: '1px solid #8b5cf640', borderRadius: 8, padding: '6px',
                cursor: 'pointer', fontFamily: 'inherit',
                width: shrunk ? 36 : '100%' }}>
              {shrunk ? '🤖' : '🤖 Настройки ИИ'}
            </button>
          )}
          {/* Смена пароля была только на странице профиля, а попасть туда можно
              было лишь нажав на своё имя — про это никто не догадывался.
              Оператору пункт не нужен: у пресса вход общий, человека называет
              ПИН, и менять общий пароль со смены нельзя. */}
          {role !== 'operator' && role !== 'investor' && (
            <button onClick={() => navigate('/profile')} title={t("Сменить пароль")} className="touch-btn" style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px', cursor: 'pointer', fontFamily: 'inherit', width: shrunk ? 36 : '100%' }}>
              {shrunk ? '🔒' : '🔒 Сменить пароль'}
            </button>
          )}
          <button onClick={handleLogout} title={t("Выйти")} style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px', cursor: 'pointer', fontFamily: 'inherit', width: shrunk ? 36 : '100%' }}>{shrunk ? '⏻' : 'Выйти'}</button>
        </div>
      </aside>

      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 24px', height: 58, borderBottom: '1px solid var(--border)', background: 'var(--card)', position: 'sticky', top: 0, zIndex: 100 }}>
          <button onClick={toggleSidebar}
            title={narrow ? 'Меню' : (collapsed ? 'Развернуть меню' : 'Свернуть меню')}
            style={{ ...hdrBtn, ...(narrow ? { width: 42, height: 42, fontSize: 19 } : null) }}>
            {narrow ? '☰' : (collapsed ? '»' : '«')}
          </button>
          <h1 style={{ fontSize: 17, fontWeight: 800, margin: 0, letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {crumb ? (
              <button onClick={() => onSection && homeKey && onSection(homeKey)}
                title={t('Вернуться на главный экран раздела')}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  font: 'inherit', color: 'var(--muted)', whiteSpace: 'nowrap',
                }}>{pageLabel}</button>
            ) : (
              <span style={{ whiteSpace: 'nowrap' }}>{pageLabel}</span>
            )}
            {crumb && <span style={{ color: 'var(--border)', fontWeight: 400 }}>›</span>}
            {crumb && (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{crumb}</span>
            )}
          </h1>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Поиск был значком-лупой в углу — про него не знали, и когда
                человек не мог найти раздел, единственное, что могло его
                выручить, выглядело как украшение. Теперь это строка со
                словом «Найти»: видно, что здесь ищут, и видно, чем открыть
                с клавиатуры. На узком экране остаётся один значок — там
                строке места нет. */}
            {narrow ? (
              <button onClick={() => setShowSearch(true)} title={t("Найти экран или заказ")} style={hdrBtn}>&#128269;</button>
            ) : (
              <button onClick={() => setShowSearch(true)} title={t("Найти экран, заказ, клиента")} style={{
                display: 'flex', alignItems: 'center', gap: 8, height: 36,
                padding: '0 12px', borderRadius: 9, cursor: 'pointer',
                border: '1px solid var(--border)', background: 'var(--bg)',
                color: 'var(--muted)', fontSize: 13, fontFamily: 'inherit',
              }}>
                <span>&#128269;</span>
                <span>{t('Найти')}</span>
                <kbd style={{
                  background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 5, padding: '1px 6px', fontSize: 10.5,
                  color: 'var(--dim)', fontFamily: 'inherit',
                }}>Ctrl K</kbd>
              </button>
            )}
            {/* Руководство по ОТКРЫТОЙ странице, а не «оглавление вообще»:
                искать свою главу в списке, стоя у пресса, никто не станет.
                Раздел страницы передаём тоже — тогда открывается его пункт. */}
            <button onClick={() => openHelp(location.pathname, section)}
              title={t("Руководство по этой странице")} style={hdrBtn}>?</button>
            {showNotifBtn && <button onClick={requestPermission} title={t("Уведомления")} style={{ ...hdrBtn, opacity: permission === 'denied' ? 0.4 : 1 }}>{permission === 'granted' ? '🔔' : '🔕'}</button>}
            <button onClick={toggle} title={t("Тема")} style={hdrBtn}>{isDark ? '☀' : '☾'}</button>
            <LangSwitch />
          </div>
        </header>

        {showNotifBtn && permission === 'default' && (
          <div style={{ background: 'var(--accent-weak)', borderBottom: '1px solid var(--border)', padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <span style={{ color: 'var(--accent)', fontSize: 13 }}>{t('Включите уведомления, чтобы не пропустить новые заявки и передачи')}</span>
            <button onClick={requestPermission} style={{ background: 'var(--accent)', border: 'none', borderRadius: 6, color: '#fff', padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t('Включить')}</button>
          </div>
        )}

        {/* Полоса тренажёра. Экраны здесь те же, до последней кнопки, и
            отличить копию от завода по внешнему виду невозможно — а разница
            в том, что тут отгрузку можно провести понарошку, а там нельзя.
            Поэтому полоса поперёк всей ширины и в каждом экране: сомнений,
            где ты находишься, быть не должно.

            Включается переменной сборки: на заводе её нет, и полосы там не
            появится, даже если этот код туда уедет. */}
        {process.env.REACT_APP_DEMO === '1' && (
          <div style={{
            background: '#f59e0b', color: '#1a1200', padding: '5px 24px',
            fontSize: 12.5, fontWeight: 800, letterSpacing: '.04em',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <span>{t('ТРЕНАЖЁР')}</span>
            <span style={{ fontWeight: 500 }}>
              {t('Копия для проверки. Настоящий завод это не видит, в 1С отсюда ничего не уходит.')}
            </span>
          </div>
        )}

        <main className="app-main" style={{
          padding: narrow ? '14px 12px 28px' : 24,
          maxWidth: 1400, margin: '0 auto', width: '100%',
        }}>
          {children}
        </main>
      </div>

      {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}
    </div>
  );
}
